import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { outreachFetch } from "../lib/business";
import { COUNTRIES } from "../lib/research";
import { parsePastedTable } from "../lib/pasteTable";

// Results found in ChatGPT (or anywhere else), pasted in. No AI call and no cost —
// the people become a research run and get the same email guessing and "Assign to
// company" as a searched run.
export default function ResearchPaste() {
  const nav = useNavigate();
  const [text, setText] = useState("");
  const [company, setCompany] = useState("");
  const [country, setCountry] = useState("");
  const [domain, setDomain] = useState("");
  const [dropped, setDropped] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsed = useMemo(() => parsePastedTable(text), [text]);
  const keep = parsed.people.filter((_, i) => !dropped.has(i));
  const distinct = new Set(keep.map(p => p.full_name.toLowerCase())).size;

  async function create() {
    setError(null);
    if (!company.trim()) { setError("Enter the company these people work at."); return; }
    if (!keep.length) { setError("Nothing to save — paste a table with a person/name column."); return; }
    setBusy(true);
    try {
      const r = await outreachFetch("/api/outreach/research/runs/import", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ company: company.trim(), country: country.trim() || undefined, domain: domain.trim() || undefined, people: keep }),
      });
      const d = await r.json() as { id?: number; error?: string };
      if (!r.ok || !d.id) { setError(d.error || `HTTP ${r.status}`); return; }
      nav(`/outreach/research/${d.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-4">
      {error && <div className="card p-4 text-sm text-rose-700">{error}</div>}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="card space-y-4 p-5">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Company</label>
            <input className="w-full !text-sm !py-2 !px-3" value={company} onChange={e => setCompany(e.target.value)} placeholder="Cellcom Israel" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Country</label>
            <input className="w-full !text-sm !py-2 !px-3" list="paste-countries" value={country} onChange={e => setCountry(e.target.value)} placeholder="Israel" />
            <datalist id="paste-countries">{COUNTRIES.map(c => <option key={c} value={c} />)}</datalist>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Email domain <span className="font-normal text-slate-400">(optional)</span></label>
            <input className="w-full !text-sm !py-2 !px-3" value={domain} onChange={e => setDomain(e.target.value)} placeholder="cellcom.co.il" />
            <p className="mt-1 text-[11px] text-slate-500">Used to guess emails when your contacts don't already show this company's format.</p>
          </div>
          <button className="btn-primary w-full" disabled={busy || !keep.length || !company.trim()} onClick={create}>
            {busy ? "Saving…" : `Save ${distinct || ""} ${distinct === 1 ? "person" : "people"} — free`}
          </button>
          <p className="text-[11px] text-slate-500">Next you'll see guessed emails, then assign people to a company.</p>
        </div>

        <div className="card space-y-3 p-5 lg:col-span-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Paste the ChatGPT table</label>
            <textarea className="h-48 w-full font-mono text-xs" value={text}
                      onChange={e => { setText(e.target.value); setDropped(new Set()); }}
                      placeholder={"Copy the whole table from ChatGPT, including the header row, and paste it here.\n\nYour category\tPerson\tCurrent/public title\tFit\nWholesale / Carrier\tDaniella Torres\tVP International Carrier Relations\tExact functional match"} />
            {parsed.columns.length > 0 && (
              <p className="mt-1 text-[11px] text-slate-500">Columns read as: {parsed.columns.join(" · ")}</p>
            )}
          </div>

          {parsed.people.length > 0 && (
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <table className="refined [&_tbody_td]:!px-3 [&_tbody_td]:!py-1.5 [&_tbody_td]:!text-xs [&_thead_th]:!px-3 [&_thead_th]:!py-1.5">
                <thead><tr><th>Person</th><th>Title</th><th>Category</th><th>Notes</th><th>Confidence</th><th>Link / email</th><th></th></tr></thead>
                <tbody>
                  {parsed.people.map((p, i) => {
                    const off = dropped.has(i);
                    return (
                      <tr key={i} className={off ? "opacity-40" : ""}>
                        <td className={`font-medium ${off ? "line-through" : ""}`}>{p.full_name}</td>
                        <td>{p.title}</td>
                        <td className="text-slate-500">{p.category}</td>
                        <td className="max-w-[220px] text-slate-500">{p.notes}</td>
                        <td><span className={`pill ${p.confidence === "high" ? "pill-green" : p.confidence === "low" ? "pill-slate" : "pill-amber"}`}>{p.confidence}</span></td>
                        <td className="max-w-[180px] truncate">
                          {p.source_url && <a className="text-indigo-600 hover:underline" href={p.source_url} target="_blank" rel="noreferrer">{p.source_url}</a>}
                          {p.email && <div className="font-mono">{p.email}</div>}
                        </td>
                        <td>
                          <button className="px-1 text-slate-400 hover:text-rose-600" title={off ? "Add back" : "Leave out"}
                                  onClick={() => { const n = new Set(dropped); if (n.has(i)) n.delete(i); else n.add(i); setDropped(n); }}>
                            {off ? "+" : "×"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {text.trim() && parsed.people.length === 0 && (
            <p className="text-sm text-amber-700">No people recognised. Make sure the header row (e.g. “Person”, “Title”) is included.</p>
          )}
          {parsed.people.length > distinct && distinct > 0 && (
            <p className="text-[11px] text-slate-500">Rows for the same person are merged into one, covering all their categories.</p>
          )}
        </div>
      </div>
    </div>
  );
}
